import {
  UpdateInspectionData,
  InspectionStatuses,
  DocumentTypes,
  InspectionSettings,
  CTFModuleTypes,
  AutoPrepJobStatus,
  Quad,
  PDFTronTools,
  InputAnnotation,
  PageRangeOption,
  DifferenceTypeNames,
  ResyncZone,
  Crossout,
  AppState,
  Job,
} from 'types';
import { createAction as createSmartAction } from 'redux-smart-actions';
import { RequestAction, QueryState, stopPolling } from '@redux-requests/core';
import {
  inspection,
  app,
  files,
  getUnmatchedPages,
  getInspectionId,
  getTotalPageCount,
  getDisplayedDifferenceGroups,
  filterDifferencesByModule,
  getSelectedModule,
  getDifferences,
  getFocusedDifference,
} from 'store';
import { AxiosError } from 'axios';
import { fetchFile } from 'store/requests';
import { PDFManagerFactory } from 'pdftron';
import store, { requestsStore } from 'store/store';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from 'queryClient';
import { inspectionsListRootKey } from 'store/queries/inspections/useInspectionsInfinite';
import {
  ApiUpdateInspection,
  ApiStartInspection,
  ApiCreateInspection,
  ApiCloneInspection,
  ApiFetchInspection,
  ApiPollInspectionStatus,
  ApiLinkedInspection,
} from './types';
import { fetchDifferences } from '../differences/actions';
import { fetchGroups } from '../difference-group/actions';
import setInspectionMarkups from './setInspectionMarkups';
import { useAutoPrepStatusStateStore } from 'zstore/autoPrepStatusStateStore';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { initialDifferenceStatusFilters } from 'store/inspection/inspectionState';
import { Difference, FetchDifferencesData } from '../differences/types';
import { createResyncMessage } from 'components/lib/GVSnackbar/ResyncSnackbarMessage';
import {
  countGroupedUnfilteredDifferences,
  groupDifferencesByGroupOrWord,
  isDifferenceFilteredOut,
} from './groupingUtils';
import { cleanupResyncZonesAndResetTool } from 'pdftron/docManager/AnnotationTools/ResyncZone';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';

function extractTextMatchJobData(ctfJobs?: Job['ctfJobs']) {
  const job = ctfJobs?.find(
    (j: { type: string; status: string }) =>
      j.type === CTFModuleTypes.textMatch && j.status === InspectionStatuses.completed,
  );
  if (!job) return { textMatchJobId: null, textMatchCdnPathPrefix: null, textMatchMetadata: null };
  const meta = job.rawResults?.metadata as Record<string, unknown> | undefined;
  return {
    textMatchJobId: job.id || null,
    textMatchCdnPathPrefix: (meta?.textMatchCdnPathPrefix as string) ?? null,
    textMatchMetadata:
      (meta?.textMatchMetadata as {
        masterPages: Record<string, number>;
        samplePages: Record<string, number>;
        totalMatches: number;
      }) ?? null,
  };
}

type UpdateLandmarkSettings = [
  InspectionSettings,
  string, // inspection id
];
export const updateLandmarkSettings = createSmartAction<RequestAction<ApiUpdateInspection>, UpdateLandmarkSettings>(
  'inspections/updateLandmarkSettings',
  (newSettings, id) => ({
    request: {
      url: `/inspections/${id}`,
      method: 'PATCH',
      data: { settings: newSettings },
    },
    meta: {
      asMutation: true,
      errorMessage: 'There was an error updating state of differences. Please try again.',
      onSuccess: (response: QueryState<ApiUpdateInspection>, _action, store) => {
        store.dispatch(inspection.actions.setUpdatedAt(response.data.updatedAt));
        return response;
      },
    },
  }),
);

const separateTypeAndStatusFilters = (filterOptions: Record<string, boolean>) => {
  const differenceTypeFilters: Record<string, boolean> = {};
  const differenceStatusFilters: Record<string, boolean> = {};

  for (const key in filterOptions) {
    // Check if the key exists in the known names object
    if (key in DifferenceTypeNames) {
      differenceTypeFilters[key] = filterOptions[key];
    } else {
      differenceStatusFilters[key] = filterOptions[key];
    }
  }

  return { differenceTypeFilters, differenceStatusFilters };
};

export const fetchInspection = createSmartAction<
  RequestAction<ApiFetchInspection>,
  [
    string, // inspection id
    'job' | 'session', // is inspection complete
  ]
>('inspections/fetchInspection', (inspectionId, jobOrSession) => {
  return {
    request: {
      url: `/inspections/${inspectionId}`,
      method: 'GET',
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache',
      },
    },
    meta: {
      errorMessage: 'There was an error. Please try again.',
      onRequest: (request, _requestAction, store) => {
        store.dispatch(inspection.actions.setDifferencesLoaded(false));
        return request;
      },
      onSuccess: (response: QueryState<ApiFetchInspection>, _action, store) => {
        const isBarcode = response.data.lastJob?.metadata?.isBarcode;

        /*
      we couple inspection SESSION and inspection JOB (results) together but we need the request data for lastjob data inside one action, so we separate the logic for two cases
        we only call this action in two instances:
        1. we fetch the new inspection JOB, where we are updating the results in which the files are already loaded. note: we are not concerned with inspection session data
        2. we fetch the inspection SESSION, where we reload the session in which the files must be loaded. note: a session may not have inspection results
       */
        if (jobOrSession === 'job') {
          // case #1: documents are already loaded, and we are fetching the new inspection data after running a new inspection
          const { status, lastJobId, lastJob, id, filterOptions, sortOptions } = response.data;
          const graphicsJobResults = lastJob?.ctfJobs.find((job) => job.type === CTFModuleTypes.graphics)?.rawResults;
          const lastJobWithImages =
            graphicsJobResults || lastJob?.ctfJobs.find((job) => job.type === CTFModuleTypes.text)?.rawResults;
          const lastJobMatches = lastJobWithImages?.matches;
          const lastJobImages = lastJobWithImages?.images;
          // Only fetch unmatchedCropIds from graphics jobs, explicitly set to empty array if no graphics job
          const lastJobUnmatchedCropIds = graphicsJobResults?.unmatchedCropIds ?? [];
          // Extract CTF version from any successful CTF job's metadata
          const lastJobCtfVersion =
            lastJob?.ctfJobs?.find((job) => job.rawResults?.metadata?.ctfVersion)?.rawResults?.metadata?.ctfVersion ||
            null;
          const { textMatchJobId, textMatchCdnPathPrefix, textMatchMetadata } = extractTextMatchJobData(
            lastJob?.ctfJobs,
          );

          store.dispatch(
            inspection.actions.setInspectionData({
              inspectionStatus: status,
              lastJobId,
              lastJobInput: lastJob?.input,
              lastJobCtfVersion,
              lastJobCreatedAt: lastJob?.createdAt ?? null,
              ctfJobs: lastJob?.ctfJobs.map((job) => job.type).filter((type) => type !== CTFModuleTypes.textMatch),
              textMatchJobId,
              textMatchCdnPathPrefix,
              textMatchMetadata,
              lastJobMatches,
              lastJobImages,
              lastJobUnmatchedCropIds,
              lastJobMetadata: lastJob?.metadata,
              settings: response.data?.settings,
              userId: response.data?.userId,
            }),
          );

          const { differenceTypeFilters } = separateTypeAndStatusFilters(filterOptions);
          store.dispatch(app.actions.setSelectedModule('All'));
          store.dispatch(app.actions.setHideDisplayPanels({ source: false, target: false }));
          store.dispatch(app.actions.setShowMarkupTab(false));
          store.dispatch(
            inspection.actions.setDifferenceViewOptions({
              sortBy: sortOptions.sortBy,
              sortByPerModule: sortOptions.sortByPerModule,
              replaceSortByPerModule: true,
              differenceTypeFilters,
              differenceStatusFilters: initialDifferenceStatusFilters, // reset status filters on new inspections
            }),
          );

          store.dispatch(fetchDifferences(id, true));
          store.dispatch(fetchGroups(id));
          PDFManagerFactory.getPDFDocManager()?.generateInspectedAnnotations(DocumentTypes.source);
          PDFManagerFactory.getPDFDocManager()?.generateInspectedAnnotations(DocumentTypes.target);

          if (isBarcode) {
            store.dispatch(inspection.actions.setIsBarcode(isBarcode));
          }

          store.dispatch(app.actions.setStartingInspection(false));
        } else {
          // When this request is called to fetch the session, it means we are updating all the inspection data. Thus, we have to clean any existing state to avoid conflicts.
          const {
            id,
            name,
            input,
            status,
            masterFileId,
            sampleFileId,
            filterOptions,
            sortOptions,
            lastJobId,
            settings,
            updatedAt,
            lastJob,
            customDictionaryIDs,
          } = response.data;
          store.dispatch(inspection.actions.resetStore());
          store.dispatch(app.actions.setIsReopenedSession(true));

          store.dispatch(files.actions.setIsFetchingInspectionFiles(true));
          // We check for input to get layers settings. This must occur BEFORE we fetch the files so that we can refer to layers when the document is loaded
          // TODO we should try to have a one set for input here instead of three separate ones.
          input?.documents.forEach((inputDoc) => {
            if (inputDoc.layers) {
              store.dispatch(
                inspection.actions.setLayers({
                  documentType: inputDoc.type === 'master' ? DocumentTypes.source : DocumentTypes.target,
                  layers: inputDoc.layers,
                }),
              );
            }
            if (inputDoc.separations) {
              store.dispatch(
                inspection.actions.setSeparations({
                  documentType: inputDoc.type === 'master' ? DocumentTypes.source : DocumentTypes.target,
                  separations: inputDoc.separations,
                }),
              );
            }
            if (inputDoc.overprint !== undefined) {
              store.dispatch(
                inspection.actions.setOverprint({
                  documentType: inputDoc.type === 'master' ? DocumentTypes.source : DocumentTypes.target,
                  enabled: inputDoc.overprint,
                }),
              );
            }
            if (inputDoc.polygons) {
              const { points, type, key } = inputDoc.polygons;
              store.dispatch(
                inspection.actions.setPolygons({
                  points,
                  documentType: inputDoc.type === 'master' ? DocumentTypes.source : DocumentTypes.target,
                  type,
                  key,
                  callSaveInspection: false,
                }),
              );
            }
          });

          let isFilesLoaded = false;
          if (masterFileId) {
            store.dispatch(fetchFile(masterFileId, DocumentTypes.source, false, id));
            isFilesLoaded = true;
          } else {
            store.dispatch(files.actions.setFileUnavailable({ documentType: DocumentTypes.source, unavailable: true }));
          }

          if (sampleFileId) {
            store.dispatch(fetchFile(sampleFileId, DocumentTypes.target, false, id));
            isFilesLoaded = true;
          } else {
            store.dispatch(files.actions.setFileUnavailable({ documentType: DocumentTypes.target, unavailable: true }));
          }

          // if the inspection has already run we fetch the results
          if (lastJobId) {
            store.dispatch(fetchDifferences(id));
            store.dispatch(fetchGroups(id));
          }

          const graphicsJobResults = lastJob?.ctfJobs.find((job) => job.type === CTFModuleTypes.graphics)?.rawResults;
          const lastJobWithImages =
            graphicsJobResults || lastJob?.ctfJobs.find((job) => job.type === CTFModuleTypes.text)?.rawResults;
          const lastJobMatches = lastJobWithImages?.matches;
          const lastJobImages = lastJobWithImages?.images;
          // Only fetch unmatchedCropIds from graphics jobs, explicitly set to empty array if no graphics job
          const lastJobUnmatchedCropIds = graphicsJobResults?.unmatchedCropIds ?? [];
          // Extract CTF version from any successful CTF job's metadata
          const lastJobCtfVersion =
            lastJob?.ctfJobs?.find((job) => job.rawResults?.metadata?.ctfVersion)?.rawResults?.metadata?.ctfVersion ||
            null;
          const { textMatchJobId, textMatchCdnPathPrefix, textMatchMetadata } = extractTextMatchJobData(
            lastJob?.ctfJobs,
          );

          // Set inspection data in store
          store.dispatch(
            inspection.actions.setInspectionData({
              id,
              name,
              inspectionStatus: status,
              lastJobId,
              lastJobInput: lastJob?.input,
              lastJobCtfVersion,
              lastJobCreatedAt: lastJob?.createdAt ?? null,
              settings,
              updatedAt,
              customDictionaryIDs,
              isFilesLoaded,
              ctfJobs: lastJob?.ctfJobs.map((job) => job.type).filter((type) => type !== CTFModuleTypes.textMatch),
              textMatchJobId,
              textMatchCdnPathPrefix,
              textMatchMetadata,
              lastJobMatches,
              lastJobImages,
              lastJobUnmatchedCropIds,
              lastJobMetadata: lastJob?.metadata,
              userId: response.data?.userId,
            }),
          );

          // @pdftron-refactor we should move this onSuccess of fetchInspectionResults as it could happen before the request is done, and that could cause errors.

          const { differenceTypeFilters, differenceStatusFilters } = separateTypeAndStatusFilters(filterOptions);

          store.dispatch(app.actions.setSelectedModule('All'));
          store.dispatch(app.actions.setHideDisplayPanels({ source: false, target: false }));
          store.dispatch(app.actions.setShowMarkupTab(false));
          store.dispatch(
            inspection.actions.setDifferenceViewOptions({
              differenceTypeFilters,
              differenceStatusFilters,
              sortBy: sortOptions?.sortBy,
              sortByPerModule: sortOptions?.sortByPerModule,
              replaceSortByPerModule: true,
            }),
          );

          if (input) {
            setInspectionMarkups(input);
          }

          if (isBarcode) {
            store.dispatch(inspection.actions.setIsBarcode(isBarcode));
          }

          if (status === InspectionStatuses.inprogress) {
            store.dispatch(pollInspectionStatus(id));
          }
        }

        if (response.data.lastJob?.metadata?.isFullPageGraphics) {
          // if full page graphics was run, we need to update what pages couldn't find a match
          const graphicsJob = response.data.lastJob?.ctfJobs.find((job) => job.type === CTFModuleTypes.graphics);
          if (graphicsJob) {
            const masterInput = response.data.input?.documents[0];
            if (!masterInput) return;
            const includedPageNumbers = masterInput.pages.filter((page) => page.included).map((page) => page.number);

            // Get pages with crops (rectangular or polygon)
            const pagesWithCrops = new Set<number>();
            masterInput.pages.forEach((page) => {
              if (page.crop && Array.isArray(page.crop) && page.crop.length > 0) {
                pagesWithCrops.add(page.number);
              }
            });

            if (graphicsJob.error) {
              const unmatchedPages = includedPageNumbers.filter(
                // if graphics job had an error, we need to specify that the included pages without crops failed
                (pageNumber) => !pagesWithCrops.has(pageNumber),
              );
              store.dispatch(inspection.actions.setUnmatchedPages(unmatchedPages));
              store.dispatch(inspection.actions.setUnmatchedCropPages([])); // No crop-specific data on error
            } else {
              const pagesWithMatches = new Set(
                graphicsJob.rawResults.matches?.map((match) => match.matchDetails[0].masterLocation.pageNumber) || [],
              );

              // Get pages with unmatched crops
              const pagesWithUnmatchedCrops = new Set<number>();
              if (graphicsJob.rawResults.unmatchedCropIds && graphicsJob.rawResults.unmatchedCropIds.length > 0) {
                masterInput.pages.forEach((page) => {
                  if (page.crop && Array.isArray(page.crop)) {
                    const hasUnmatchedCrop = page.crop.some((crop) =>
                      graphicsJob.rawResults.unmatchedCropIds?.includes(crop.cropId || ''),
                    );
                    if (hasUnmatchedCrop) {
                      pagesWithUnmatchedCrops.add(page.number);
                    }
                  }
                });
              }

              const unmatchedPages = includedPageNumbers.filter((pageNumber) => {
                // Page is unmatched if:
                // 1. It's a full page graphics page (no crops) that has no match
                // 2. OR it has unmatched crops
                const isFullPageWithoutMatch = !pagesWithCrops.has(pageNumber) && !pagesWithMatches.has(pageNumber);
                const hasUnmatchedCrops = pagesWithUnmatchedCrops.has(pageNumber);
                return isFullPageWithoutMatch || hasUnmatchedCrops;
              });

              store.dispatch(inspection.actions.setUnmatchedPages(unmatchedPages));
              store.dispatch(inspection.actions.setUnmatchedCropPages(Array.from(pagesWithUnmatchedCrops)));
            }
          }
        } else {
          // full page graphics was not run, there are no unmatched pages
          store.dispatch(inspection.actions.setUnmatchedPages([]));
          store.dispatch(inspection.actions.setUnmatchedCropPages([]));
        }

        const unmatchedPages = getUnmatchedPages(store.getState());
        if (jobOrSession === 'job' && unmatchedPages.length > 0) {
          window.analytics.track('Graphics-failure', {
            inspectionID: inspectionId,
            'Failure-count': unmatchedPages.length,
            'Pages-failed': unmatchedPages,
          });
        }

        return response;
      },
    },
  };
});

export const pollInspectionStatus = createSmartAction<
  RequestAction<ApiFetchInspection>,
  [
    string, // inspection id
  ]
>('inspections/pollInspectionStatus', (inspectionId) => ({
  request: {
    url: `/inspections/status/${inspectionId}`,
    method: 'GET',
  },
  meta: {
    poll: 1,
    errorMessage: 'There was an error loading the results. Please try again',
    onSuccess: (response: QueryState<ApiPollInspectionStatus>, _action, store) => {
      const inspectionStatus = response.data;

      if (
        inspectionStatus === InspectionStatuses.completed ||
        inspectionStatus === InspectionStatuses.error ||
        inspectionStatus === InspectionStatuses.idle // we stop polling once we receive back that we have cancelled the inspection
      ) {
        store.dispatch(stopPolling([pollInspectionStatus.toString()]));
        store.dispatch(inspection.actions.setInspectionData({ inspectionStatus }));
      }

      if (inspectionStatus === InspectionStatuses.error) {
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'There was an error loading the results. Please try again',
            type: 'error',
          }),
        );
        store.dispatch(app.actions.setStartingInspection(false));
      }

      if (inspectionStatus === InspectionStatuses.completed) {
        requestsStore.dispatchRequest(fetchInspection(inspectionId, 'job')); // fetch the inspection results once inspection has completed

        // Refetch linked inspections to update status/lastJobId in dropdown
        const state = store.getState();
        const groupId = state.inspection.settings?.groupId;
        if (groupId) {
          store.dispatch(fetchLinkedInspections(groupId));

          // A linked inspection inherits the group's share state server-side when
          // it completes. Its collaborators query may have been cached (empty)
          // before the run, so invalidate it to refetch the inherited share state
          // (otherwise the ShareModal shows "shared" with no collaborators listed).
          queryClient.invalidateQueries({ queryKey: ['collaborators', inspectionId] });
        }
      }

      return response;
    },
    onError: (error, _requestAction, store) => {
      store.dispatch(app.actions.setStartingInspection(false));
      return error;
    },
  },
}));

export const cancelInspection = createSmartAction<
  RequestAction<string>,
  [
    string, // inspection id
  ]
>('inspections/cancel', (inspectionId) => ({
  request: {
    url: `/inspections/${inspectionId}/cancel`,
    method: 'PATCH',
  },
  meta: {
    errorMessage: 'There was an error. Please try again.',
    onRequest: (request, _requestAction, store) => {
      store.dispatch(stopPolling([pollInspectionStatus.toString()]));
      store.dispatch(inspection.actions.setDifferencesLoaded(false));
      return request;
    },
    onSuccess: (response: QueryState<string>, _action) => {
      if (response.data === InspectionStatuses.finalizing) {
        store.dispatch(pollInspectionStatus(inspectionId));
      } else {
        store.dispatch(fetchInspection(inspectionId, 'job'));
      }
      return response;
    },
  },
}));

type StartInspectionPayload = [
  string, // inspection id
  {
    noLiveText: boolean;
  },
];
// TEMP: currently being named runInspection because this is called through a side effect of 'startInspection'
export const runInspection = createSmartAction<RequestAction<ApiStartInspection>, StartInspectionPayload>(
  'inspections/startInspection',
  (id, data) => ({
    payload: {
      request: {
        url: `/inspections/${id}/start`,
        method: 'POST',
        data,
      },
    },
    meta: {
      errorMessage: 'There was an error. Please try again.', // TODO: get correct message
      asMutation: true,
      onSuccess: (response: QueryState<ApiStartInspection>, _action, store) => {
        store.dispatch(inspection.actions.setInspectionData({ inspectionStatus: InspectionStatuses.queued }));
        store.dispatch(pollInspectionStatus(id));
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        store.dispatch(app.actions.setStartingInspection(false));
        throw error;
      },
    },
  }),
);

export interface CreateInspectionData {
  masterFileId?: string;
  sampleFileId?: string;
  name?: string;
  id?: string;
  settings?: InspectionSettings;
}
export const createInspection = createSmartAction<RequestAction<ApiCreateInspection>, [CreateInspectionData]>(
  'inspections/createInspection',
  (data) => ({
    request: {
      url: '/inspections',
      method: 'POST',
      data,
    },
    meta: {
      asMutation: true,
      errorMessage: 'There was an error creating the inspection. Please try again.',
      onSuccess: (response: QueryState<ApiCreateInspection>, _action, store) => {
        const { id, name, status, updatedAt, settings } = response.data;
        const oldSettings = store.getState().inspection.settings;
        store.dispatch(
          inspection.actions.setInspectionData({
            id,
            name,
            inspectionStatus: status,
            lastJobId: null,
            updatedAt,
            settings: { ...oldSettings, ...settings },
            userId: response.data?.userId,
          }),
        );
        store.dispatch(app.actions.setAutosaving(false));
        store.dispatch(app.actions.setUpdatingInspection(false));
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        // if inspection creation has failed we need to clean up the state of files
        store.dispatch(files.actions.resetStore());
        store.dispatch(app.actions.setUpdatingInspection(false));

        throw error;
      },
    },
  }),
);

export interface CloneInspectionData {
  newName?: string;
}

export const cloneInspection = createSmartAction<RequestAction<ApiCloneInspection>>(
  'inspections/cloneInspection',
  (inspectionId, data, onSuccessCallback?: (data: ApiCloneInspection) => void) => {
    // Generate unique operation ID for tracking
    const operationId = uuidv4();

    // Start tracking cloning operation
    store.dispatch(
      app.actions.startCloningOperation({
        id: operationId,
        inspectionId,
        inspectionName: data?.newName,
        startTime: new Date(),
      }),
    );

    return {
      request: {
        url: `/inspections/${inspectionId}/clone`,
        method: 'POST',
        data,
      },
      meta: {
        errorMessage: 'There was an error cloning the inspection. Please try again.',
        onSuccess: (response: QueryState<ApiCloneInspection>, _action, store) => {
          const { id, name, status, updatedAt } = response.data;

          // Finish tracking this operation
          store.dispatch(app.actions.finishCloningOperation(operationId));

          queryClient.invalidateQueries({ queryKey: inspectionsListRootKey });

          store.dispatch(
            app.actions.setSnackMessage({
              message: `Inspection "${name}" has been successfully cloned!`,
              type: 'success',
            }),
          );

          // Call the callback function if provided
          if (onSuccessCallback) {
            onSuccessCallback(response.data);
          }

          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          // Finish tracking this operation on error
          store.dispatch(app.actions.finishCloningOperation(operationId));

          // Check for specific error types
          let errorMessage = 'Failed to clone inspection. Please try again.';
          if (error.response?.status === 422) {
            errorMessage = "You don't have permissions to clone this inspection.";
          }

          store.dispatch(
            app.actions.setSnackMessage({
              message: errorMessage,
              type: 'error',
            }),
          );
          throw error;
        },
      },
    };
  },
);

export interface CopyPrepInspectionData {
  newName?: string;
}

export const copyPrepInspection = createSmartAction<RequestAction<ApiCloneInspection>>(
  'inspections/copyPrepInspection',
  (inspectionId, data, onSuccessCallback?: (data: ApiCloneInspection) => void) => {
    return {
      request: {
        url: `/inspections/${inspectionId}/copy-prep`,
        method: 'POST',
        data,
      },
      meta: {
        errorMessage: 'There was an error copying the inspection prep. Please try again.',
        onSuccess: (response: QueryState<ApiCloneInspection>, _action, store) => {
          const { name, settings } = response.data;

          queryClient.invalidateQueries({ queryKey: inspectionsListRootKey });

          // Refetch linked inspections if the new inspection is part of a group
          if (settings?.groupId) {
            store.dispatch(fetchLinkedInspections(settings.groupId));
          }

          store.dispatch(
            app.actions.setSnackMessage({
              message: `Inspection prep "${name}" has been successfully copied!`,
              type: 'success',
            }),
          );

          // Call the callback function if provided
          if (onSuccessCallback) {
            onSuccessCallback(response.data);
          }

          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          // Check for specific error types
          let errorMessage = 'Failed to copy inspection prep. Please try again.';
          if (error.response?.status === 401) {
            errorMessage = 'You can only copy prep from inspections within your organization.';
          } else if (error.response?.status === 404) {
            errorMessage = 'Inspection not found.';
          }

          store.dispatch(
            app.actions.setSnackMessage({
              message: errorMessage,
              type: 'error',
            }),
          );
          throw error;
        },
      },
    };
  },
);

// QUESTION: currently the three actions below (updateInspection, updateInspectionFiles, updateDifferenceViewOptions) are all the same endpoint, however were being used as separate actions with different side effects
type UpdateInspectionPayload = [
  string, // inspection id
  UpdateInspectionData,
];
export const updateInspection = createSmartAction<RequestAction<ApiUpdateInspection>, UpdateInspectionPayload>(
  'inspections/updateInspection',
  (id, data) => ({
    request: {
      url: `/inspections/${id}`,
      method: 'PATCH',
      data,
    },
    meta: {
      asMutation: true,
      errorMessage: 'There was an error updating the inspection. Please try again.',
      onRequest: (request, _action, store) => {
        // Track concurrent updateInspection requests to manage autosaving flag correctly
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        pendingUpdateInspectionCount += 1;
        store.dispatch(app.actions.setAutosaving(true));
        const { input } = data;
        if (input) {
          setInspectionMarkups(input);
        }
        return request;
      },
      onSuccess: (response, _action, store) => {
        if (useAutoPrepStatusStateStore.getState().autoPrepStatus === AutoPrepJobStatus.pending) {
          const inspectionId = getInspectionId(store.getState());
          store.dispatch(startAutoPrepJob(inspectionId));
        }
        // Update inspection status from backend response
        // For imported inspections, backend keeps it as Complete on first PDF metadata update
        if (response?.data?.status) {
          store.dispatch(inspection.actions.setInspectionData({ inspectionStatus: response.data.status }));
        }
        // autosave complete only when no more concurrent updateInspection are in flight
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        pendingUpdateInspectionCount = Math.max(0, pendingUpdateInspectionCount - 1);
        if (pendingUpdateInspectionCount === 0) {
          store.dispatch(app.actions.setAutosaving(false));
        }
        return response;
      },
      onError: (error, _action, store) => {
        // ensure autosaving UI resets when final concurrent request errors
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        pendingUpdateInspectionCount = Math.max(0, pendingUpdateInspectionCount - 1);
        if (pendingUpdateInspectionCount === 0) {
          store.dispatch(app.actions.setAutosaving(false));
        }
        return error;
      },
    },
  }),
);

// Module-level tracker for concurrent updateInspection requests
let pendingUpdateInspectionCount = 0;

// Tracks the displayed results count immediately before a resync
let preResyncResultsCount = 0;

// Stores differences before resync to adjust count when filters are enabled
let preResyncDifferences: Difference[] = [];

// Snapshots of counts prior to a resync operation, used to rollback on errors
let preResyncCountSnapshot = 0;
let preCumulativeNetResyncedDifferencesSnapshot = 0;

// Helper: snapshot current resync-related counts
const snapshotResyncCounts = (store: any) => {
  const settingsBefore = store.getState().inspection.settings;
  preResyncCountSnapshot = settingsBefore.resyncCount ?? 0;
  preCumulativeNetResyncedDifferencesSnapshot = settingsBefore.cumulativeNetResyncedDifferences ?? 0;
};

// Helper: revert resync-related counts if they differ from the snapshot
const revertResyncCountsIfChanged = (store: any) => {
  const currentSettings = store.getState().inspection.settings;
  const currentResyncCount = currentSettings.resyncCount ?? 0;
  const currentCumulative = currentSettings.cumulativeNetResyncedDifferences ?? 0;
  if (
    currentResyncCount !== preResyncCountSnapshot ||
    currentCumulative !== preCumulativeNetResyncedDifferencesSnapshot
  ) {
    const inspectionId = getInspectionId(store.getState());
    store.dispatch(inspection.actions.setResyncCount(preResyncCountSnapshot));
    store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(preCumulativeNetResyncedDifferencesSnapshot));
    store.dispatch(
      updateInspection(inspectionId, {
        settings: {
          ...currentSettings,
          resyncCount: preResyncCountSnapshot,
          cumulativeNetResyncedDifferences: preCumulativeNetResyncedDifferencesSnapshot,
        },
      }),
    );
  }
};

// RequestKey -> failure flag map to prevent onSuccess increments after an error
const resyncFailureByRequestKey = new Map<string, boolean>();

type UpdateInspectionFilesPayload = [
  string, // inspection Id
  UpdateInspectionData,
];
export const updateInspectionFiles = createSmartAction<
  RequestAction<ApiUpdateInspection>,
  UpdateInspectionFilesPayload
>('inspection/updateInspectionFiles', (id, data) => ({
  payload: {
    request: {
      url: `/inspections/${id}`,
      method: 'PATCH',
      data,
    },
  },
  meta: {
    asMutation: true,
    errorMessage: 'There was an error updating the inspection. Please try again.',
    onRequest: (request, _requestAction, store) => {
      store.dispatch(app.actions.setUpdatingInspection(false));
      return request;
    },
    onError: (error, _requestAction, store) => {
      if (error) {
        store.dispatch(fetchInspection(id, 'session'));
      }
      throw error;
    },
  },
}));

type UpdateDifferenceViewOptionsPayload = [
  Pick<UpdateInspectionData, 'sortOptions' | 'filterOptions'>,
  string, // inspection id
  string?, // request key
];
export const updateDifferenceViewOptions = createSmartAction<
  RequestAction<ApiUpdateInspection>,
  UpdateDifferenceViewOptionsPayload
>('inspections/updateDifferenceViewOptions', (data, id, requestKey) => ({
  request: {
    url: `/inspections/${id}`,
    method: 'PATCH',
    data,
  },
  meta: {
    asMutation: true,
    requestKey: requestKey || 'updateDifferenceViewOptions',
    errorMessage: 'There was an error updating state of differences. Please try again.',
    onSuccess: (response: QueryState<ApiUpdateInspection>, _action, store) => {
      if (data.filterOptions || data.sortOptions) {
        store.dispatch(inspection.actions.setUpdatedAt(response.data.updatedAt));
      }
      return response;
    },
  },
}));

type UpdateHighlightColorPayload = [
  Pick<UpdateInspectionData, 'settings'>,
  string, // inspection id
];
export const updateInspectionHighlightColor = createSmartAction<
  RequestAction<ApiUpdateInspection>,
  UpdateHighlightColorPayload
>('inspections/updateInspectionHighlightColor', (data, id) => ({
  request: {
    url: `/inspections/${id}`,
    method: 'PATCH',
    data,
  },
  meta: {
    asMutation: true,
    requestKey: 'updateInspectionHighlightColor',
    errorMessage: 'There was an error updating the highlight color. Please try again.',
    onSuccess: (response: QueryState<ApiUpdateInspection>, _action, store) => {
      if (response.data.settings.highlightColor) {
        store.dispatch(inspection.actions.setHighlightColor(response.data.settings.highlightColor));
      }

      return response;
    },
  },
}));

// Necessary to update something in inspection settings without triggering the outdated inspection warning
type UpdateSharingStatusPayload = [
  Pick<UpdateInspectionData, 'settings'>,
  string, // inspection id
];
export const updateInspectionSharingStatus = createSmartAction<
  RequestAction<ApiUpdateInspection>,
  UpdateSharingStatusPayload
>('inspections/updateInspectionSharingStatus', (data, id) => ({
  request: {
    url: `/inspections/${id}`,
    method: 'PATCH',
    data,
  },
  meta: {
    asMutation: true,
    requestKey: 'updateInspectionSharingStatus',
    errorMessage: 'There was an error updating the sharing status. Please try again.',
    onSuccess: (response: QueryState<ApiUpdateInspection>, _action, store) => {
      if (response.data.settings.isSharing !== undefined) {
        store.dispatch(inspection.actions.setIsSharing(response.data.settings.isSharing));
      }

      return response;
    },
  },
}));

type StartAutoPrepJobPayload = [inspectionId: string];
export const startAutoPrepJob = createSmartAction<RequestAction<any>, StartAutoPrepJobPayload>(
  'inspections/startAutoPrepJob',
  (inspectionId: string) => {
    useAutoPrepStatusStateStore.getState().setAutoPrepStatus(AutoPrepJobStatus.inprogress);
    return {
      request: {
        url: `inspections/autoPrep/${inspectionId}`,
        method: 'POST',
      },
      meta: {
        onSuccess: (response: QueryState<any>, _requestAction, store) => {
          const job = response.data;
          store.dispatch(pollAutoPrepJobStatus(job?.id));
          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          useAutoPrepStatusStateStore.getState().setAutoPrepStatus(AutoPrepJobStatus.error);
          return error;
        },
      },
    };
  },
);

type ResyncData = {
  differencesToResync: Difference[];
  sourceResyncZone: ResyncZone;
  targetResyncZone: ResyncZone;
  orphanedDiffs: {
    targetOrphaned: Difference[];
    sourceOrphaned: Difference[];
  };
};

export const resyncAnnotations = createSmartAction<
  RequestAction<any>,
  [
    string, // inspection id
    {
      sourceResyncZone: ResyncZone;
      targetResyncZone: ResyncZone;
      differencesToResync: Difference[];
      resyncCount: number;
      orphanedDiffs: {
        targetOrphaned: Difference[];
        sourceOrphaned: Difference[];
      };
      crossouts: {
        sourceCrossouts: Crossout[];
        targetCrossouts: Crossout[];
      };
    },
  ]
>('inspections/resyncAnnotations', (inspectionId, data) => {
  const { differencesToResync, sourceResyncZone, targetResyncZone, resyncCount, orphanedDiffs, crossouts } = data;

  return {
    request: {
      url: `/inspections/${inspectionId}/resync`,
      method: 'POST',
      data: {
        sourceResyncZone,
        targetResyncZone,
        resyncCount,
        differencesToResync,
        crossouts,
      },
    },
    meta: {
      asMutation: true,
      onRequest: (request, _action, store) => {
        // Show loading only for full resync; fast resync path does not trigger this action
        store.dispatch(inspection.actions.setResyncLoading(true));
        store.dispatch(inspection.actions.setResyncCancelled(false));
        return request;
      },
      onSuccess: (response: QueryState<any>, _action, store) => {
        const { jobId } = response.data;
        const resyncData = {
          differencesToResync,
          sourceResyncZone,
          targetResyncZone,
          orphanedDiffs,
        };
        store.dispatch(pollResyncJobStatus(jobId, inspectionId, resyncData));
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        store.dispatch(inspection.actions.setResyncLoading(false));
        // Clear any existing success toast types to avoid visual overlap
        store.dispatch(app.actions.clearSnackMessageType('success'));
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Re-inspect failed. Please try again.',
            type: 'error',
          }),
        );
        throw error;
      },
    },
  };
});

type PollResyncJobStatusPayload = [jobId: string, inspectionId: string, resyncData: ResyncData];
export const pollResyncJobStatus = createSmartAction<RequestAction<boolean>, PollResyncJobStatusPayload>(
  'inspections/pollResyncJobStatus',
  (jobId, inspectionId, resyncData) => {
    return {
      request: {
        url: `/inspections/resync/status/${jobId}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        onSuccess: (response: QueryState<boolean>, _requestAction, store) => {
          // Check if resync has been cancelled
          const resyncCancelled = store.getState().inspection.resyncStatus.cancelled;
          if (resyncCancelled) {
            // Resync was cancelled, stop polling
            store.dispatch(stopPolling([pollResyncJobStatus.toString()]));
            return response;
          }

          if (response.data) {
            store.dispatch(stopPolling([pollResyncJobStatus.toString()]));
            const resyncCount = store.getState().inspection.settings.resyncCount;
            store.dispatch(inspection.actions.setProcessingResyncResults(true));
            store.dispatch(processResyncResponse(jobId, inspectionId, resyncCount, resyncData));
          }
          return response;
        },
        onError: (error: AxiosError, _requestAction, store) => {
          store.dispatch(inspection.actions.setResyncLoading(false));
          store.dispatch(stopPolling([pollResyncJobStatus.toString()]));
          // Clear any existing success toast types to avoid visual overlap
          store.dispatch(app.actions.clearSnackMessageType('success'));
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'Re-inspect failed. Please try again.',
              type: 'error',
            }),
          );
          return error;
        },
      },
    };
  },
);

type FastResyncExcludePayload = [
  {
    inspectionId: string;
    differenceIds: string[];
    resyncCount: number;
    orphanedDiffs?: {
      targetOrphaned: Difference[];
      sourceOrphaned: Difference[];
    };
    sourceResyncZone?: ResyncZone; // for tracking only
    targetResyncZone?: ResyncZone; // for tracking only
    eliminatedTypeBreakdown?: Record<string, number>; // for tracking only
  },
];
export const fastResyncExcludeDifferences = createSmartAction<
  RequestAction<{ updated: number; updatedDifferenceList: Difference[]; trackingData?: any }>,
  FastResyncExcludePayload
>('inspections/fastResyncExcludeDifferences', (data) => ({
  request: {
    url: `/inspections/fast-resync`,
    method: 'POST',
    data: {
      inspectionId: data.inspectionId,
      differenceIds: data.differenceIds,
      resyncCount: data.resyncCount,
      orphanedDiffs: data.orphanedDiffs,
    },
  },
  meta: {
    asMutation: true,
    onRequest: (request, _action, store) => {
      store.dispatch(inspection.actions.setResyncLoading(true));
      store.dispatch(inspection.actions.setResyncCancelled(false));
      preResyncResultsCount = getDisplayedDifferenceGroups(store.getState()).length;
      snapshotResyncCounts(store);
      const reqKey = `${fastResyncExcludeDifferences.toString()}::${Date.now()}::${Math.random()}`;
      (request as any).__resyncRequestKey = reqKey;
      resyncFailureByRequestKey.set(reqKey, false);
      return request;
    },
    onSuccess: (
      response: QueryState<{ updated: number; updatedDifferenceList: Difference[]; trackingData?: any }>,
      requestAction,
      store,
    ) => {
      const req = (requestAction?.payload?.request || {}) as any;
      const reqKey = req.__resyncRequestKey as string | undefined;
      if (reqKey && resyncFailureByRequestKey.get(reqKey)) {
        return response;
      }
      // Guard: only proceed on a valid success payload
      if (!Array.isArray(response.data?.updatedDifferenceList)) {
        return response;
      }
      const eliminatedCount = (data.differenceIds || []).length;
      const createdCount = response.data?.trackingData?.createdTotalCount || 0;
      const netDifferencesCount = createdCount - eliminatedCount;

      // Get new results count for toast message
      const { differenceTypeFilters, differenceStatusFilters } = store.getState().inspection.differenceViewOptions;
      const selectedModule = getSelectedModule(store.getState());
      const allNewDifferences = response.data?.updatedDifferenceList || [];

      // Filter by module to match preResyncResultsCount (which is module-aware)
      const moduleFilteredDifferences = filterDifferencesByModule(allNewDifferences, selectedModule);
      const newResultsCount = countGroupedUnfilteredDifferences(
        moduleFilteredDifferences,
        differenceTypeFilters,
        differenceStatusFilters,
      );
      const resultsDelta = newResultsCount - preResyncResultsCount;
      const message = createResyncMessage(resultsDelta);
      const inspectionId = getInspectionId(store.getState());
      const updatedSettings = store.getState().inspection.settings;

      try {
        window.analytics?.track('reinspectPerformed', {
          type: 'fastReinspect',
          userEmail: getTrackerMetadata(store.getState()).userEmail,
          inspectionID: data.inspectionId,
          reinspectCount: updatedSettings.resyncCount,
          sourceCoordinates: data.sourceResyncZone,
          newCoordinates: data.targetResyncZone,
          DifferencesEliminated: {
            totalCount: eliminatedCount,
            ...(data.eliminatedTypeBreakdown || {}),
          },
          DifferencesCreated: {
            totalCount: createdCount,
            ...(response.data?.trackingData?.createdTypeBreakdown || {}),
          },
        });
      } catch (e) {
        console.warn('Fast reinspect analytics tracking failed', e);
      }

      // Increment resync count and cumulative net resynced differences
      const newResyncCount = (updatedSettings.resyncCount ?? 0) + 1;
      const newCumulativeNetResyncedDifferences =
        (updatedSettings.cumulativeNetResyncedDifferences ?? 0) + netDifferencesCount;
      store.dispatch(
        updateInspection(inspectionId, {
          settings: {
            ...updatedSettings,
            resyncCount: newResyncCount,
            cumulativeNetResyncedDifferences: newCumulativeNetResyncedDifferences,
          },
        }),
      );
      store.dispatch(inspection.actions.setResyncCount(newResyncCount));
      store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(newCumulativeNetResyncedDifferences));

      store.dispatch(
        app.actions.setSnackMessage({
          message,
          type: 'success',
          autoHideDuration: null,
          customAction: {
            label: 'Undo re-inspect',
            onClick: () => {
              store.dispatch(inspection.actions.setUndoingResync(true));
              store.dispatch(undoLastResync(inspectionId, netDifferencesCount));
            },
          },
        }),
      );

      store.dispatch(inspection.actions.setResyncLoading(false));
      cleanupResyncZonesAndResetTool();

      return response;
    },
    onError: (error, requestAction, store) => {
      const req = (requestAction?.payload?.request || {}) as any;
      const reqKey = req.__resyncRequestKey as string | undefined;
      if (reqKey) resyncFailureByRequestKey.set(reqKey, true);
      // Ensure UI is reset even if fast resync fails
      store.dispatch(inspection.actions.setResyncLoading(false));
      cleanupResyncZonesAndResetTool();
      // Revert any counts incremented prior to the error (if applicable)
      revertResyncCountsIfChanged(store);
      // Restore differences list from server to undo optimistic UI changes
      try {
        store.dispatch(fetchDifferences(data.inspectionId, true));
      } catch {}
      // Clear any existing success toast types to avoid visual overlap
      store.dispatch(app.actions.clearSnackMessageType('success'));
      store.dispatch(
        app.actions.setSnackMessage({
          message: 'Re-inspect failed. Please try again.',
          type: 'error',
        }),
      );
      return error;
    },
    mutations: {
      [fetchDifferences.toString()]: {
        updateData: (
          _data: FetchDifferencesData,
          mutationData?: { updatedDifferenceList?: Difference[] },
        ): FetchDifferencesData => {
          const list = mutationData?.updatedDifferenceList;
          return list || _data || [];
        },
        updateDataOptimistic: (differences: FetchDifferencesData): FetchDifferencesData => {
          if (!differences) return differences;
          const idsToRemove = new Set(data.differenceIds || []);
          const filtered = differences.filter((difference) => !idsToRemove.has(difference.id));

          const orphaned = [
            ...(data.orphanedDiffs?.sourceOrphaned || []),
            ...(data.orphanedDiffs?.targetOrphaned || []),
          ];

          if (orphaned.length === 0) return filtered;
          return [...filtered, ...orphaned];
        },
      },
    },
  },
}));

type ProcessResyncResponsePayload = [jobId: string, inspectionId: string, resyncCount: number, resyncData: ResyncData];
type MutationData = {
  updatedDifferenceList: Difference[];
  resyncResults: any;
};
export const processResyncResponse = createSmartAction<RequestAction<any>, ProcessResyncResponsePayload>(
  'inspections/processResyncResponse',
  (jobId, inspectionId, resyncCount, resyncData) => {
    return {
      request: {
        url: `/inspections/resync/${jobId}`,
        method: 'POST',
        data: {
          resyncCount,
          originalDifferences: resyncData.differencesToResync,
          orphanedDiffs: resyncData.orphanedDiffs,
        },
      },
      meta: {
        onRequest: (request, _requestAction, store) => {
          preResyncResultsCount = getDisplayedDifferenceGroups(store.getState()).length;
          // Store differences before resync to adjust count when filters are enabled
          preResyncDifferences = getDifferences(store.getState());
          snapshotResyncCounts(store);
          const reqKey = `${processResyncResponse.toString()}::${Date.now()}::${Math.random()}`;
          (request as any).__resyncRequestKey = reqKey;
          resyncFailureByRequestKey.set(reqKey, false);
          return request;
        },
        onSuccess: (response: QueryState<any>, requestAction, store) => {
          const req = (requestAction?.payload?.request || {}) as any;
          const reqKey = req.__resyncRequestKey as string | undefined;
          if (reqKey && resyncFailureByRequestKey.get(reqKey)) {
            return response;
          }
          // Guard: only proceed on a valid success payload
          if (!Array.isArray(response.data?.updatedDifferenceList) || !response.data?.trackingData) {
            return response;
          }
          const {
            eliminatedTotalCount,
            eliminatedTypeBreakdown,
            createdTotalCount,
            createdTypeBreakdown,
            netDifferencesCount,
          } = response.data.trackingData;

          window.analytics.track('reinspectPerformed', {
            type: 'ctfReinspect',
            userEmail: getTrackerMetadata(store.getState()).userEmail,
            inspectionID: inspectionId,
            reinspectCount: resyncCount,
            sourceCoordinates: resyncData.sourceResyncZone,
            newCoordinates: resyncData.targetResyncZone,
            DifferencesEliminated: { totalCount: eliminatedTotalCount, ...eliminatedTypeBreakdown },
            DifferencesCreated: { totalCount: createdTotalCount, ...createdTypeBreakdown },
          });

          // Get new results count for toast message
          const { differenceTypeFilters, differenceStatusFilters } = store.getState().inspection.differenceViewOptions;

          // Enable difference type filters for any newly created difference types that are currently disabled
          if (createdTypeBreakdown) {
            const updatedTypeFilters = { ...differenceTypeFilters };
            let hasFilterChanges = false;
            const enabledFilterTypes: string[] = [];

            // Check each type in createdTypeBreakdown
            for (const [typeName, count] of Object.entries(createdTypeBreakdown)) {
              if (
                typeof count === 'number' &&
                count > 0 &&
                typeName in DifferenceTypeNames &&
                differenceTypeFilters[typeName] === true
              ) {
                updatedTypeFilters[typeName] = false;
                hasFilterChanges = true;
                enabledFilterTypes.push(typeName);
              }
            }

            // Adjust preResyncResultsCount for filters being enabled
            // Count differences of enabled types that existed before resync
            if (enabledFilterTypes.length > 0) {
              const selectedModule = getSelectedModule(store.getState());
              const preResyncModuleFiltered = filterDifferencesByModule(preResyncDifferences, selectedModule);

              // Get groups that were already visible before resync (using original filters)
              // This prevents double-counting groups that merge into existing visible groups
              const preResyncUnfiltered = preResyncModuleFiltered.filter(
                (diff) => !isDifferenceFilteredOut(diff, differenceTypeFilters, differenceStatusFilters),
              );
              const preResyncVisibleGroups = groupDifferencesByGroupOrWord(preResyncUnfiltered);
              const preResyncVisibleGroupIds = new Set(
                preResyncVisibleGroups.map((diff) => diff.word || diff.groupId).filter(Boolean),
              );

              // Count differences of the enabled types that weren't filtered out by status filters
              const enabledTypeDifferences = preResyncModuleFiltered.filter(
                (diff) =>
                  enabledFilterTypes.includes(diff.type) &&
                  !isDifferenceFilteredOut(diff, updatedTypeFilters, differenceStatusFilters),
              );

              // Count grouped differences of enabled types, excluding groups that were already visible
              const enabledTypeGroups = groupDifferencesByGroupOrWord(enabledTypeDifferences);
              const newlyVisibleGroupsCount = enabledTypeGroups.filter(
                (diff) => !preResyncVisibleGroupIds.has(diff.word || diff.groupId || ''),
              ).length;

              // Add to preResyncResultsCount (only groups that weren't already visible)
              preResyncResultsCount += newlyVisibleGroupsCount;
            }

            // Update filters if any changes were made
            if (hasFilterChanges) {
              store.dispatch(
                inspection.actions.setDifferenceViewOptions({
                  differenceTypeFilters: updatedTypeFilters,
                }),
              );
              store.dispatch(
                updateDifferenceViewOptions(
                  {
                    filterOptions: {
                      ...updatedTypeFilters,
                      ...differenceStatusFilters,
                    },
                  },
                  inspectionId,
                  'updateFiltersAfterResync',
                ),
              );
            }
          }

          // Get updated filters after potential changes
          const currentTypeFilters = store.getState().inspection.differenceViewOptions.differenceTypeFilters;
          const selectedModule = getSelectedModule(store.getState());
          const allNewDifferences = response.data?.updatedDifferenceList || [];

          // Filter by module to match preResyncResultsCount (which is module-aware)
          const moduleFilteredDifferences = filterDifferencesByModule(allNewDifferences, selectedModule);
          const newDisplayedCount = countGroupedUnfilteredDifferences(
            moduleFilteredDifferences,
            currentTypeFilters,
            differenceStatusFilters,
          );
          const resultsDelta = newDisplayedCount - preResyncResultsCount;
          const message = createResyncMessage(resultsDelta);

          // Update resync count and cumulative net resync differences in backend and in store
          const updatedSettings = store.getState().inspection.settings;
          const newCumulativeNetResyncedDifferences =
            (updatedSettings.cumulativeNetResyncedDifferences ?? 0) + netDifferencesCount;
          const newResyncCount = resyncCount + 1;

          store.dispatch(
            updateInspection(inspectionId, {
              settings: {
                ...updatedSettings,
                resyncCount: newResyncCount,
                cumulativeNetResyncedDifferences: newCumulativeNetResyncedDifferences,
              },
            }),
          );
          store.dispatch(inspection.actions.setResyncCount(newResyncCount));
          store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(newCumulativeNetResyncedDifferences));

          // Delay success toast slightly to allow any error to surface first; skip if an error toast is active/queued
          setTimeout(() => {
            const appState = store.getState().app as AppState;
            const hasErrorToastActive = appState?.snackMessage?.type === 'error';
            const hasErrorToastQueued = (appState?.snackMessageQueue || []).some((m: any) => m.type === 'error');
            if (hasErrorToastActive || hasErrorToastQueued) {
              return;
            }
            store.dispatch(
              app.actions.setSnackMessage({
                message,
                type: 'success',
                autoHideDuration: null, // persist until dismissed; provides time to undo
                customAction: {
                  label: 'Undo re-inspect',
                  onClick: () => {
                    // If the newly created resync diff is focused, unfocus it before undoing the resync
                    const currentState = store.getState();
                    const focusedDifference = getFocusedDifference(currentState);
                    const currentSettings = currentState.inspection.settings;
                    const resyncCountBeingUndone = (currentSettings.resyncCount ?? 0) - 1;

                    if (focusedDifference?.resyncId === resyncCountBeingUndone) {
                      store.dispatch(inspection.actions.unfocusDifference());
                    }

                    // Optimistic update: revert counts immediately without showing a loading screen
                    const optimisticNewResyncCount = Math.max(0, resyncCountBeingUndone);
                    const optimisticCumulative =
                      (currentSettings.cumulativeNetResyncedDifferences ?? 0) - netDifferencesCount;
                    store.dispatch(inspection.actions.setResyncCount(optimisticNewResyncCount));
                    store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(optimisticCumulative));
                    store.dispatch(undoLastResync(inspectionId, netDifferencesCount));
                  },
                },
              }),
            );
          }, 500);

          store.dispatch(inspection.actions.setResyncLoading(false));

          // Clean up resync zones and reset tool after successful resync
          cleanupResyncZonesAndResetTool();

          // Clear preResyncDifferences
          preResyncDifferences = [];

          return response;
        },
        onError: (error: AxiosError, requestAction, store) => {
          const req = (requestAction?.payload?.request || {}) as any;
          const reqKey = req.__resyncRequestKey as string | undefined;
          if (reqKey) resyncFailureByRequestKey.set(reqKey, true);
          store.dispatch(inspection.actions.setResyncLoading(false));

          // Clean up resync zones and reset tool even on error
          cleanupResyncZonesAndResetTool();

          // Revert any counts incremented prior to the error (if applicable)
          revertResyncCountsIfChanged(store);
          // Clear any existing success toast types to avoid visual overlap
          store.dispatch(app.actions.clearSnackMessageType('success'));
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'Re-inspect failed. Please try again.',
              type: 'error',
            }),
          );

          // Clear preResyncDifferences
          preResyncDifferences = [];

          return error;
        },
        mutations: {
          [fetchDifferences.toString()]: (
            _data: FetchDifferencesData,
            mutationData?: MutationData,
          ): FetchDifferencesData => {
            const list = mutationData?.updatedDifferenceList;
            return list || _data || [];
          },
        },
      },
    };
  },
);

type PollAutoPrepJobStatusPayload = [jobId: string];
export const pollAutoPrepJobStatus = createSmartAction<RequestAction<any>, PollAutoPrepJobStatusPayload>(
  'inspections/pollAutoPrepJobStatus',
  (jobId) => {
    return {
      request: {
        url: `inspections/autoPrep/status/${jobId}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        onSuccess: (response: QueryState<boolean>, _requestAction, store) => {
          if (useAutoPrepStatusStateStore.getState().autoPrepStatus === AutoPrepJobStatus.cancelled) {
            store.dispatch(stopPolling([pollAutoPrepJobStatus.toString()]));
          } else if (response.data) {
            store.dispatch(stopPolling([pollAutoPrepJobStatus.toString()]));
            store.dispatch(fetchAutoPrepResponse(jobId));
          }
          return response;
        },
        onError: (error: AxiosError, _requestAction, store) => {
          store.dispatch(stopPolling([pollAutoPrepJobStatus.toString()]));
          useAutoPrepStatusStateStore.getState().setAutoPrepStatus(AutoPrepJobStatus.error);
          return error;
        },
      },
    };
  },
);

type FetchAutoPrepResponsePayload = [jobId: string];
export const fetchAutoPrepResponse = createSmartAction<RequestAction<any>, FetchAutoPrepResponsePayload>(
  'inspections/fetchAutoPrepResponse',
  (jobId) => {
    return {
      request: {
        url: `inspections/autoprep/${jobId}`,
        method: 'GET',
      },
      meta: {
        onSuccess: async (response: QueryState<any>, _requestAction, store) => {
          const { masterPageNumbers, textZones } = response.data;

          const state = store.getState();
          const masterTotalPageCount = getTotalPageCount(store.getState()).source;

          if (masterTotalPageCount > masterPageNumbers.length) {
            const webViewer = PDFManagerFactory.getViewer(DocumentTypes.source);
            store.dispatch(inspection.actions.setPageRangeOption(PageRangeOption.RANGE));
            store.dispatch(
              inspection.actions.setPageRange({
                documentType: DocumentTypes.source,
                pageRange: masterPageNumbers,
              }),
            );
            webViewer?.setCurrentPageNumber(masterPageNumbers[0]);
          }

          const sourceAnnotations: InputAnnotation[] = [];
          const targetAnnotations: InputAnnotation[] = [];

          textZones.forEach(
            (
              zone: {
                master: { quads: Quad[]; pageNumber: number; id: string };
                sample: { quads: Quad[]; pageNumber: number; id: string };
              },
              i: number,
            ) => {
              const masterZone: InputAnnotation = {
                annotationId: zone.master.id,
                quads: zone.master.quads,
                page: zone.master.pageNumber,
                usedTool: PDFTronTools.MARQUEE_ZONE,
                number: state.inspection.nextMarqueeZoneId + i,
              };

              const sampleZone: InputAnnotation = {
                annotationId: zone.sample.id,
                quads: zone.sample.quads,
                page: zone.sample.pageNumber,
                usedTool: PDFTronTools.MARQUEE_ZONE,
                number: state.inspection.nextMarqueeZoneId + i,
              };

              sourceAnnotations.push(masterZone);
              targetAnnotations.push(sampleZone);
            },
          );

          // existing text markups prior to running auto-prep have been removed but we want to keep any existing crop-zones
          const sourceInputAnnotations = PDFAnnotationManager.getInputAnnotations(DocumentTypes.source) || [];
          const targetInputAnnotations = PDFAnnotationManager.getInputAnnotations(DocumentTypes.target) || [];

          store.dispatch(
            inspection.actions.setAnnotations({
              documentType: DocumentTypes.source,
              annotations: [...sourceInputAnnotations, ...sourceAnnotations],
            }),
          );
          store.dispatch(
            inspection.actions.setAnnotations({
              documentType: DocumentTypes.target,
              annotations: [...targetInputAnnotations, ...targetAnnotations],
            }),
          );

          PDFManagerFactory?.getPDFDocManager()?.reloadMarqueeZones(sourceAnnotations, targetAnnotations);

          useAutoPrepStatusStateStore.getState().setAutoPrepStatus(AutoPrepJobStatus.completed);

          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          console.log({ error });
          useAutoPrepStatusStateStore.getState().setAutoPrepStatus(AutoPrepJobStatus.error);
        },
      },
    };
  },
);

type UndoLastResyncPayload = [inspectionId: string, lastNetDifferencesCount: number];
type UndoResyncMutationData = {
  updatedDifferenceList: Difference[];
  undoResults: any;
};
export const undoLastResync = createSmartAction<RequestAction<any>, UndoLastResyncPayload>(
  'inspections/undoLastResync',
  (inspectionId, lastNetDifferencesCount) => {
    return {
      request: {
        url: `/inspections/${inspectionId}/undo-resync`,
        method: 'POST',
        data: {
          lastNetDifferencesCount,
        },
      },
      meta: {
        asMutation: true,
        errorMessage: 'There was an error undoing the re-inspect operation. Please try again.',
        onSuccess: (response: QueryState<any>, _requestAction, store) => {
          const { success, newResyncCount } = response.data;
          if (success) {
            // Update the resync count in the store
            const currentSettings = store.getState().inspection.settings;
            const newCumulativeNetResyncedDifferences =
              (currentSettings.cumulativeNetResyncedDifferences ?? 0) - lastNetDifferencesCount;
            store.dispatch(
              updateInspection(inspectionId, {
                settings: {
                  ...currentSettings,
                  resyncCount: newResyncCount,
                  cumulativeNetResyncedDifferences: newCumulativeNetResyncedDifferences,
                },
              }),
            );
            store.dispatch(inspection.actions.setResyncCount(newResyncCount));
            store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(newCumulativeNetResyncedDifferences));

            store.dispatch(
              app.actions.setSnackMessage({
                message: `Undo successful`,
                type: 'success',
              }),
            );

            window.analytics.track('reinspectUndo', {
              userEmail: getTrackerMetadata(store.getState()).userEmail,
              inspectionID: inspectionId,
              newReinspectCount: newResyncCount,
              netChangeInDifferencesCount: lastNetDifferencesCount,
            });
          } else {
            store.dispatch(
              app.actions.setSnackMessage({
                message: 'Failed to undo re-inspect operation.',
                type: 'error',
              }),
            );
          }

          return response;
        },
        onError: (error: AxiosError, _requestAction, store) => {
          // Roll back optimistic updates
          const currentSettings = store.getState().inspection.settings;
          const rollbackResyncCount = (currentSettings.resyncCount ?? 0) + 1;
          const rollbackCumulative = (currentSettings.cumulativeNetResyncedDifferences ?? 0) + lastNetDifferencesCount;
          store.dispatch(inspection.actions.setResyncCount(rollbackResyncCount));
          store.dispatch(inspection.actions.setCumulativeNetResyncedDifferences(rollbackCumulative));
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'Failed to undo re-inspect operation. Please try again.',
              type: 'error',
            }),
          );
          throw error;
        },
        mutations: {
          [fetchDifferences.toString()]: (
            _data: FetchDifferencesData,
            mutationData: UndoResyncMutationData,
          ): FetchDifferencesData => {
            return mutationData.updatedDifferenceList;
          },
        },
      },
    };
  },
);

export const fetchLinkedInspections = createSmartAction<
  RequestAction<ApiLinkedInspection[]>,
  [string] // groupId
>('inspections/fetchLinkedInspections', (groupId) => ({
  request: {
    url: `/inspections/linked/${groupId}`,
    method: 'GET',
  },
  meta: {
    errorMessage: 'Failed to fetch linked inspections',
    requestKey: `fetchLinkedInspections-${groupId}`,
  },
}));
